import { TuiElasticContainer } from '@taiga-ui/layout';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@environments/environment';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { DepartmentPickerComponent } from '@modules/department/components/department-picker/department-picker.component';
import { WOUNDTRACK_PREFIXES } from '@modules/forms/form-modules/woundtrack/config/woundtrack-export.config';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { UserSequenceGalleryService } from '@modules/gallery/services/dialogs/sliders/sequences/user-sequence-gallery.service';
import { SelectLabelComponent } from '@modules/label/select-label/select-label.component';
import { PatientEditComponent } from '@modules/patient/patient-edit/patient-edit/patient-edit.component';
import { PaywallAlertComponent } from '@modules/paywall/paywall-alert/paywall-alert.component';
import { SadmAnalysisInputComponent } from '@modules/sadm/components/sadm-analysis-input/sadm-analysis-input.component';
import { SadmEntityService } from '@modules/sadm/services/sadm-entity.service';
import { getLatestProtocol } from '@modules/sadm/utils/get-latest-protocol';
import { ImageInputComponent } from '@modules/shared/image-input/image-input.component';
import { SpinnerComponent } from '@modules/shared/spinner/spinner.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Media, Patient } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { FormAnswer } from '@pixacare/forms';
import { StorageService } from '@services/storage.service';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { AutoCompleteLabelModel } from '@shared/models/helpers/auto-complete-label-model';
import { StorageSpace } from '@shared/models/paywall/storage-space.enum';
import { selectIsLongVideoEnabled } from '@shared/store/clients/clients.selectors';
import { selectUserEnabledClientDepartmentIds } from '@shared/store/departments/departments.selectors';
import { selectSadmClients } from '@shared/store/sadm/sadm.selectors';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { selectSequence } from '@shared/store/sequences/sequences.selectors';
import { MAX_MEDIA_UPLOAD_SIZE } from '@shared/utils/max-media-upload-size';
import { TuiButton, TuiCalendar, TuiDialogContext, TuiError, TuiInput } from '@taiga-ui/core';
import { TuiButtonLoading, TuiInputDateTime, TuiTextarea } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { BehaviorSubject, combineLatest, filter, first, map, Observable, of } from 'rxjs';
import { getDropZoneAcceptValidator } from '../drop-zone-accept-validator';
import { isImagePayloadTooLarge } from '../services/is-image-payload-too-large';
import { SequenceEditService } from './sequence-edit.service';

@UntilDestroy()
@Component({
  selector: 'pxc-sequence-edit',
  templateUrl: './sequence-edit.component.html',
  providers: [UserSequenceGalleryService, SequenceEditService, SadmEntityService, DepartmentPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiElasticContainer,
    TranslocoDirective,
    AsyncPipe,
    PaywallAlertComponent,
    ImageInputComponent,
    FormsModule,
    ReactiveFormsModule,
    SadmAnalysisInputComponent,
    SpinnerComponent,
    PatientEditComponent,
    SelectLabelComponent,
    DepartmentPickerComponent,
    TuiInput,
    TuiInputDateTime,
    TuiTextarea,
    TuiButton,
    TuiButtonLoading,
    TuiCalendar,
    TuiError,
  ],
})
export class SequenceEditComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly sequenceEditService = inject(SequenceEditService);
  private readonly userSequenceGalleryService = inject(UserSequenceGalleryService);
  private readonly storageService = inject(StorageService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly context = inject<
    TuiDialogContext<
      void,
      {
        sequenceId: number;
        clientCode: string;
      }
    >
  >(POLYMORPHEUS_CONTEXT, { optional: true });

  @Input() sequenceId: number = this.context?.data.sequenceId;
  @Input() clientCode: string = this.context?.data.clientCode;

  storageSpace: StorageSpace = StorageSpace.AVAILABLE;

  // Patient
  updatedPatient: Patient;
  basePatient: Patient;

  // Labels
  updatedLabels: AutoCompleteLabelModel[];

  // Date
  updatedDate: Date;

  // Medias
  newMedias: File[] = [];
  medias: Media[];
  deletedMediasIds: number[] = [];

  // Description
  updatedDescription: string;

  // Form state
  isPatientEditFormLoading$ = new BehaviorSubject<boolean>(false);
  isPatientEditFormValid$ = new BehaviorSubject<boolean>(true);

  // Analysis
  displayAnalysis$: Observable<boolean>;
  sadmEntityId: number;
  sadmEntityDraft: SadmEntityDraft;
  analysisFormAnswer$ = new BehaviorSubject<FormAnswer>(null);
  protocolFormAnswer$ = new BehaviorSubject<FormAnswer>(null);
  isSadmFormLoading$ = of(false);
  selectedDepartmentIds: number[] = [];

  userId = this.authenticationService.user.id;

  sequence$: Observable<FlattenedSequence> = this.store
    .select(selectSequence(this.sequenceId))
    .pipe(
      filter((sequence) =>
        this.clientCode === environment.commonClientCode ? true : !!sequence.sequenceInstance.departmentIds,
      ),
    );

  isDepartmentChoiceDisabled$ = combineLatest([
    this.sequence$,
    this.store.select(selectUserEnabledClientDepartmentIds(this.clientCode, this.userId)),
  ]).pipe(
    map(([{ sequenceInstance }, enabledDepartmentIds]) => {
      if (sequenceInstance.departmentIds.every((id) => enabledDepartmentIds.includes(id))) {
        return sequenceInstance.createdBy !== this.userId;
      }
      return true;
    }),
    untilDestroyed(this),
  );

  protected readonly isLongVideoEnabled = toSignal(this.store.select(selectIsLongVideoEnabled(this.clientCode)), {
    initialValue: false,
  });
  readonly dropZoneAcceptValidator = computed(() => getDropZoneAcceptValidator(this.isLongVideoEnabled()));
  dropZoneMaxFileSize = MAX_MEDIA_UPLOAD_SIZE;

  get pictureCount(): number {
    return this.medias.length + this.newMedias.length;
  }

  // Only the medias added in this edition are sent to attachSequenceMedia — the ones already stored
  // server-side don't weigh on that request.
  get hasTooLargeImagePayload(): boolean {
    return isImagePayloadTooLarge(this.newMedias);
  }

  get formInputs(): ModuleResolverInputs {
    return {
      medias: [...this.medias],
      files: [...this.newMedias],
    };
  }

  ngOnInit(): void {
    this.storageService.getActiveClientStorageSpace().subscribe((storageSpace) => (this.storageSpace = storageSpace));

    this.store
      .select(selectSequence(this.sequenceId))
      .pipe(untilDestroyed(this), first())
      .subscribe((sequence) => {
        if (!sequence?.sequenceInstance?.departmentIds) {
          this.store.dispatch(
            sequencesActions.getSequence({
              clientCode: this.clientCode,
              sequenceId: this.sequenceId,
            }),
          );
        }
      });

    // Initialize labels and date with info from the sequence
    this.sequence$.pipe(first()).subscribe((sequence) => {
      this.updatedLabels = sequence.labels.map<AutoCompleteLabelModel>((label) => ({
        value: label,
        display: label.word,
      }));

      this.updatedDate = new Date(sequence.sequenceInstance.createdOn);
      this.updatedPatient = sequence.patientInstance;
      this.basePatient = sequence.patientInstance;
      this.updatedDescription = sequence.sequenceInstance.description;
      this.selectedDepartmentIds = sequence.sequenceInstance.departmentIds || [];

      this.sadmEntityId = sequence.sequenceInstance.sadmEntityId;

      this.analysisFormAnswer$ = new BehaviorSubject<FormAnswer>(sequence.sequenceInstance.formAnswer);

      this.protocolFormAnswer$.next(
        getLatestProtocol(sequence?.sadmEntity?.protocolsHistory)?.protocolCreationFormAnswer,
      );

      if (sequence.sequenceInstance.formAnswerId && !sequence.sequenceInstance.formAnswer) {
        this.store.dispatch(
          sequencesActions.getSequenceAnalysis({
            clientCode: sequence.clientCode,
            sequenceId: sequence.sequenceInstance.id,
          }),
        );
        this.isSadmFormLoading$ = this.sequence$.pipe(
          map((s) => s.sequenceInstance.formAnswerId && !s.sequenceInstance.formAnswer),
        );
      }
    });

    this.sequence$.pipe(untilDestroyed(this)).subscribe((sequence) => {
      this.medias = sequence.medias;
      this.analysisFormAnswer$.next(sequence.sequenceInstance.formAnswer);
      this.changeDetectorRef.detectChanges();
    });

    // Analysis
    this.displayAnalysis$ = combineLatest([this.store.select(selectSadmClients), this.analysisFormAnswer$]).pipe(
      map(([clients, analysisFormAnswer]) => !!analysisFormAnswer || clients.length > 0),
    );
  }

  patientChange(patient: Patient): void {
    this.updatedPatient = patient;
  }

  arePicturesValid(): boolean {
    return this.pictureCount > 0 && !this.hasTooLargeImagePayload;
  }

  save(): void {
    if (!this.arePicturesValid()) {
      return;
    }

    combineLatest([this.sequence$, this.analysisFormAnswer$, this.protocolFormAnswer$])
      .pipe(first())
      .subscribe(([sequence, analysisFormAnswer, protocolCreationFormAnswer]) => {
        if (this.isSequenceTouched(sequence, analysisFormAnswer)) {
          this.sequenceEditService.save({
            updatedPatient: this.updatedPatient,
            updatedLabels: this.updatedLabels,
            updatedDate: this.updatedDate,
            updatedDescription: this.updatedDescription,
            removedMediasId: this.deletedMediasIds,
            newMedias: this.newMedias,
            clientCode: this.clientCode,
            sadmEntityId: this.sadmEntityId,
            sadmEntityDraft: this.sadmEntityDraft,
            sequence,
            analysisFormAnswer,
            protocolCreationFormAnswer,
            departmentIds: this.selectedDepartmentIds,
          });
        }

        this.close();
      });
  }

  close(): void {
    if (this.context) {
      this.context.completeWith();
    }
  }

  openGallery(mediaIdx: number): void {
    this.sequence$.pipe(first()).subscribe((sequence) => {
      this.userSequenceGalleryService.openGallery(sequence.clientCode, sequence.sequenceInstance.id, mediaIdx);
    });
  }

  deleteMedia(mediaId: number): void {
    this.medias = this.medias.filter((media) => media.id !== mediaId);
    this.deletedMediasIds.push(mediaId);
  }

  addSequenceMedia(event: { addedFiles: File[] }): void {
    this.newMedias.push(...event.addedFiles);

    // Run change detection for this specific action
    setInterval(() => {
      this.changeDetectorRef.detectChanges();
    });
  }

  removeSequenceMedia(index: number): void {
    this.newMedias.splice(index, 1);
  }

  isSequenceTouched(sequence: FlattenedSequence, analysisFormAnswer: FormAnswer): boolean {
    return this.sequenceEditService.isSequenceTouched(sequence, {
      updatedPatient: this.updatedPatient,
      updatedLabels: this.updatedLabels,
      updatedDate: this.updatedDate,
      updatedDescription: this.updatedDescription,
      removedMediasId: this.deletedMediasIds,
      newMedias: this.newMedias,
      clientCode: this.clientCode,
      analysisFormAnswer,
      sadmEntityId: this.sadmEntityId,
      sadmEntityDraft: this.sadmEntityDraft,
      departmentIds: this.selectedDepartmentIds,
    });
  }

  onOutputPicturesChange(files: File[]): void {
    if (!files?.length) {
      return;
    }

    const previousFiles = this.newMedias.filter(
      (file) => !WOUNDTRACK_PREFIXES.some((prefix) => file.name.startsWith(prefix)),
    );
    this.newMedias = [...previousFiles, ...files];
  }

  canConfirmSequenceEdition(isPatientEditFormValid: boolean, isPatientEditFormLoading: boolean): boolean {
    const isDepartmentsIdsLengthInvalid = this.selectedDepartmentIds.length === 0 && this.clientCode !== 'common';
    return (
      !isPatientEditFormValid ||
      isPatientEditFormLoading ||
      this.pictureCount === 0 ||
      this.hasTooLargeImagePayload ||
      isDepartmentsIdsLengthInvalid
    );
  }
}
